import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  ApplicationSource,
  BuildPack,
  CoolifyServerDto,
  CreateApplicationInput,
  GitBranchDto,
  GithubAppDto,
  GitRepositoryDto,
  PlanFeatures,
  PlanLimits,
  ResourceDto,
  SubscriptionDto,
} from '@cpm/shared';
import { BUILD_PACKS } from '@cpm/shared';
import { ArrowLeft, ArrowRight, Rocket } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client.js';
import {
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  cx,
} from '../components/ui.js';

interface SubscriptionDetailResponse {
  subscription: SubscriptionDto;
  limits: PlanLimits;
  features: PlanFeatures;
}

const SOURCE_OPTIONS: Array<{ key: ApplicationSource; label: string; hint: string }> = [
  { key: 'public', label: 'Public repository', hint: 'Any public git URL' },
  { key: 'github-app', label: 'Private repository (GitHub App)', hint: 'Pick from installed GitHub Apps' },
  { key: 'deploy-key', label: 'Private repository (deploy key)', hint: 'Git URL + a Coolify private key' },
  { key: 'dockerfile', label: 'Dockerfile', hint: 'Paste a Dockerfile, no repository' },
  { key: 'dockerimage', label: 'Docker image', hint: 'Deploy an existing registry image' },
];

const GIT_SOURCES: ApplicationSource[] = ['public', 'github-app', 'deploy-key'];

interface WizardState {
  source: ApplicationSource;
  serverUuid: string;
  name: string;
  description: string;
  gitRepository: string;
  gitBranch: string;
  githubAppId: number | null;
  githubAppUuid: string;
  privateKeyUuid: string;
  dockerfile: string;
  dockerImageName: string;
  dockerImageTag: string;
  buildPack: BuildPack;
  installCommand: string;
  buildCommand: string;
  startCommand: string;
  baseDirectory: string;
  publishDirectory: string;
  domainsText: string;
  autogenerateDomain: boolean;
  portsExposes: string;
  healthCheckEnabled: boolean;
  healthCheckPath: string;
  healthCheckPort: string;
  instantDeploy: boolean;
}

const INITIAL: WizardState = {
  source: 'public',
  serverUuid: '',
  name: '',
  description: '',
  gitRepository: '',
  gitBranch: 'main',
  githubAppId: null,
  githubAppUuid: '',
  privateKeyUuid: '',
  dockerfile: '',
  dockerImageName: '',
  dockerImageTag: 'latest',
  buildPack: 'nixpacks',
  installCommand: '',
  buildCommand: '',
  startCommand: '',
  baseDirectory: '',
  publishDirectory: '',
  domainsText: '',
  autogenerateDomain: true,
  portsExposes: '',
  healthCheckEnabled: false,
  healthCheckPath: '/',
  healthCheckPort: '',
  instantDeploy: false,
};

function toCreateInput(state: WizardState, subscriptionId: string): CreateApplicationInput {
  const domains = state.domainsText
    .split('\n')
    .map((d) => d.trim())
    .filter(Boolean);
  const common = {
    subscriptionId,
    serverUuid: state.serverUuid,
    name: state.name,
    description: state.description || null,
    domains: domains.length ? domains : undefined,
    autogenerateDomain: state.autogenerateDomain,
    portsExposes: state.portsExposes || undefined,
    healthCheck: state.healthCheckEnabled
      ? {
          enabled: true,
          path: state.healthCheckPath || undefined,
          port: state.healthCheckPort || undefined,
        }
      : undefined,
    instantDeploy: state.instantDeploy,
  };
  const git = {
    gitRepository: state.gitRepository,
    gitBranch: state.gitBranch,
    buildPack: state.buildPack,
    installCommand: state.installCommand || undefined,
    buildCommand: state.buildCommand || undefined,
    startCommand: state.startCommand || undefined,
    baseDirectory: state.baseDirectory || undefined,
    publishDirectory: state.publishDirectory || undefined,
  };
  switch (state.source) {
    case 'public':
      return { source: 'public', ...common, ...git };
    case 'github-app':
      return { source: 'github-app', githubAppUuid: state.githubAppUuid, ...common, ...git };
    case 'deploy-key':
      return { source: 'deploy-key', privateKeyUuid: state.privateKeyUuid, ...common, ...git };
    case 'dockerfile':
      return { source: 'dockerfile', dockerfile: state.dockerfile, ...common };
    case 'dockerimage':
      return {
        source: 'dockerimage',
        dockerImageName: state.dockerImageName,
        dockerImageTag: state.dockerImageTag || 'latest',
        ...common,
      };
  }
}

function GithubRepositoryPicker({
  state,
  update,
}: {
  state: WizardState;
  update: (patch: Partial<WizardState>) => void;
}) {
  const apps = useQuery({
    queryKey: ['github-apps'],
    queryFn: () => api.get<GithubAppDto[]>('/api/applications/github-apps'),
  });
  const repos = useQuery({
    queryKey: ['github-apps', state.githubAppId, 'repositories'],
    queryFn: () =>
      api.get<GitRepositoryDto[]>(
        `/api/applications/github-apps/${state.githubAppId}/repositories`,
      ),
    enabled: state.githubAppId !== null,
  });
  const [owner, repoName] = state.gitRepository.includes('/')
    ? [state.gitRepository.split('/')[0], state.gitRepository.split('/').slice(1).join('/')]
    : ['', ''];
  const branches = useQuery({
    queryKey: ['github-apps', state.githubAppId, 'branches', state.gitRepository],
    queryFn: () =>
      api.get<GitBranchDto[]>(
        `/api/applications/github-apps/${state.githubAppId}/branches?owner=${encodeURIComponent(owner!)}&repo=${encodeURIComponent(repoName)}`,
      ),
    enabled: state.githubAppId !== null && Boolean(owner && repoName),
  });

  if (apps.isPending) return <Spinner label="Loading GitHub Apps…" />;
  if (apps.error) return <ErrorNote error={apps.error} />;
  if (apps.data.length === 0) {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400">
        No GitHub Apps are configured in Coolify. Add one under Coolify → Sources first.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="gh-app">GitHub App</Label>
        <Select
          id="gh-app"
          value={state.githubAppId ?? ''}
          onChange={(e) => {
            const app = apps.data.find((a) => a.id === Number(e.target.value));
            update({
              githubAppId: app?.id ?? null,
              githubAppUuid: app?.uuid ?? '',
              gitRepository: '',
            });
          }}
        >
          <option value="" disabled>
            Select a GitHub App…
          </option>
          {apps.data.map((app) => (
            <option key={app.id} value={app.id}>
              {app.name}
            </option>
          ))}
        </Select>
      </div>
      {state.githubAppId !== null && (
        <div>
          <Label htmlFor="gh-repo">Repository</Label>
          {repos.isPending ? (
            <Spinner label="Loading repositories…" />
          ) : repos.error ? (
            <ErrorNote error={repos.error} />
          ) : (
            <Select
              id="gh-repo"
              value={state.gitRepository}
              onChange={(e) => {
                const repo = repos.data.find((r) => r.fullName === e.target.value);
                update({
                  gitRepository: e.target.value,
                  gitBranch: repo?.defaultBranch ?? 'main',
                  name: state.name || repo?.name || '',
                });
              }}
            >
              <option value="" disabled>
                Select a repository…
              </option>
              {repos.data.map((repo) => (
                <option key={repo.id} value={repo.fullName}>
                  {repo.fullName}
                </option>
              ))}
            </Select>
          )}
        </div>
      )}
      {state.gitRepository && (
        <div>
          <Label htmlFor="gh-branch">Branch</Label>
          {branches.isPending ? (
            <Spinner label="Loading branches…" />
          ) : branches.error ? (
            <ErrorNote error={branches.error} />
          ) : (
            <Select
              id="gh-branch"
              value={state.gitBranch}
              onChange={(e) => update({ gitBranch: e.target.value })}
            >
              {branches.data.map((branch) => (
                <option key={branch.name} value={branch.name}>
                  {branch.name}
                </option>
              ))}
            </Select>
          )}
        </div>
      )}
    </div>
  );
}

export function ApplicationCreatePage() {
  const { id: subscriptionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const staticStarter = searchParams.get('starter') === 'static';
  const [state, setState] = useState<WizardState>(() => ({
    ...INITIAL,
    ...(staticStarter ? { buildPack: 'static' as const } : {}),
  }));
  const [step, setStep] = useState(0);
  const update = (patch: Partial<WizardState>) => setState((s) => ({ ...s, ...patch }));

  const detail = useQuery({
    queryKey: ['subscriptions', subscriptionId],
    queryFn: () => api.get<SubscriptionDetailResponse>(`/api/subscriptions/${subscriptionId}`),
    enabled: Boolean(subscriptionId),
  });
  const servers = useQuery({
    queryKey: ['coolify', 'servers'],
    queryFn: () => api.get<CoolifyServerDto[]>('/api/coolify/servers'),
  });

  const create = useMutation({
    mutationFn: (input: CreateApplicationInput) =>
      api.post<{ application: ResourceDto; warning: string | null }>('/api/applications/', input),
    onSuccess: (result) => {
      void navigate(`/applications/${result.application.id}`);
    },
  });

  if (detail.isPending) return <Spinner />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  const { subscription, features } = detail.data;

  const isGit = GIT_SOURCES.includes(state.source);
  const steps = isGit
    ? ['Source', 'Build', 'Network & health', 'Review']
    : ['Source', 'Network & health', 'Review'];
  const stepKey = steps[Math.min(step, steps.length - 1)];

  const sourceComplete =
    state.serverUuid !== '' &&
    state.name.trim() !== '' &&
    (state.source === 'public' || state.source === 'deploy-key'
      ? state.gitRepository.trim() !== '' &&
        state.gitBranch.trim() !== '' &&
        (state.source !== 'deploy-key' || state.privateKeyUuid.trim() !== '')
      : state.source === 'github-app'
        ? state.githubAppUuid !== '' && state.gitRepository !== '' && state.gitBranch !== ''
        : state.source === 'dockerfile'
          ? state.dockerfile.trim() !== ''
          : state.dockerImageName.trim() !== '');

  const canNext = stepKey === 'Source' ? sourceComplete : true;

  return (
    <div>
      <Link
        to={`/subscriptions/${subscriptionId}`}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <ArrowLeft className="size-4" /> {subscription.name}
      </Link>
      <PageHeader
        title="New application"
        description={
          <>
            Provisioned into the Coolify project of <strong>{subscription.name}</strong> — plan
            limits apply.
          </>
        }
      />

      {!subscription.coolifyProjectUuid && (
        <div className="mb-4">
          <ErrorNote error="This subscription has no linked Coolify project — applications cannot be provisioned." />
        </div>
      )}

      {staticStarter && (
        <div className="mb-4 rounded-lg bg-brand-50 px-4 py-3 text-sm text-brand-800 ring-1 ring-inset ring-brand-200 dark:bg-brand-500/10 dark:text-brand-200 dark:ring-brand-500/30">
          Portable static-site starter selected. Choose a reviewed Git source and keep the
          static build pack, or use the migration workflow for captured Plesk document-root
          files. Forms, commerce and external editor state require separate destinations.
        </div>
      )}

      <ol className="mb-6 flex flex-wrap items-center gap-2 text-sm">
        {steps.map((label, index) => (
          <li key={label} className="flex items-center gap-2">
            {index > 0 && <span className="text-slate-300 dark:text-slate-600">→</span>}
            <span
              className={cx(
                'flex items-center gap-1.5 rounded-full px-3 py-1',
                index === step
                  ? 'bg-brand-600 font-medium text-white'
                  : index < step
                    ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-400'
                    : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
              )}
            >
              {index + 1}. {label}
            </span>
          </li>
        ))}
      </ol>

      <Card>
        <CardHeader title={stepKey} />
        <div className="space-y-5 px-5 py-5">
          {stepKey === 'Source' && (
            <>
              <div>
                <Label>Source type</Label>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {SOURCE_OPTIONS.map((option) => (
                    <button
                      type="button"
                      key={option.key}
                      onClick={() => update({ source: option.key })}
                      className={cx(
                        'rounded-lg border p-3 text-left text-sm transition-colors',
                        state.source === option.key
                          ? 'border-brand-600 bg-brand-50 dark:border-brand-400 dark:bg-brand-500/10'
                          : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600',
                      )}
                    >
                      <span className="block font-medium">{option.label}</span>
                      <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                        {option.hint}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="app-name">Name</Label>
                  <Input
                    id="app-name"
                    required
                    value={state.name}
                    onChange={(e) => update({ name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="app-server">Server</Label>
                  {servers.isPending ? (
                    <Spinner label="Loading servers…" />
                  ) : servers.error ? (
                    <ErrorNote error={servers.error} />
                  ) : (
                    <Select
                      id="app-server"
                      value={state.serverUuid}
                      onChange={(e) => update({ serverUuid: e.target.value })}
                    >
                      <option value="" disabled>
                        Select a server…
                      </option>
                      {servers.data.map((server) => (
                        <option key={server.uuid} value={server.uuid} disabled={!server.usable}>
                          {server.name} ({server.ip}){server.usable ? '' : ' — not usable'}
                        </option>
                      ))}
                    </Select>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="app-desc">Description (optional)</Label>
                <Input
                  id="app-desc"
                  value={state.description}
                  onChange={(e) => update({ description: e.target.value })}
                />
              </div>

              {(state.source === 'public' || state.source === 'deploy-key') && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="app-repo">Git repository URL</Label>
                    <Input
                      id="app-repo"
                      placeholder="https://github.com/acme/web"
                      value={state.gitRepository}
                      onChange={(e) => update({ gitRepository: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="app-branch">Branch</Label>
                    <Input
                      id="app-branch"
                      value={state.gitBranch}
                      onChange={(e) => update({ gitBranch: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {state.source === 'deploy-key' && (
                <div>
                  <Label htmlFor="app-key">Coolify private key UUID</Label>
                  <Input
                    id="app-key"
                    placeholder="From Coolify → Security → Private Keys"
                    value={state.privateKeyUuid}
                    onChange={(e) => update({ privateKeyUuid: e.target.value })}
                  />
                </div>
              )}

              {state.source === 'github-app' && (
                <GithubRepositoryPicker state={state} update={update} />
              )}

              {state.source === 'dockerfile' && (
                <div>
                  <Label htmlFor="app-dockerfile">Dockerfile</Label>
                  <Textarea
                    id="app-dockerfile"
                    rows={10}
                    className="font-mono text-xs"
                    placeholder={'FROM nginx:alpine\nCOPY . /usr/share/nginx/html'}
                    value={state.dockerfile}
                    onChange={(e) => update({ dockerfile: e.target.value })}
                  />
                </div>
              )}

              {state.source === 'dockerimage' && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="app-image">Image</Label>
                    <Input
                      id="app-image"
                      placeholder="ghcr.io/acme/api"
                      value={state.dockerImageName}
                      onChange={(e) => update({ dockerImageName: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="app-tag">Tag</Label>
                    <Input
                      id="app-tag"
                      value={state.dockerImageTag}
                      onChange={(e) => update({ dockerImageTag: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {stepKey === 'Build' && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="app-buildpack">Build pack</Label>
                  <Select
                    id="app-buildpack"
                    value={state.buildPack}
                    onChange={(e) => update({ buildPack: e.target.value as BuildPack })}
                  >
                    {BUILD_PACKS.map((pack) => (
                      <option key={pack} value={pack}>
                        {pack}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="app-basedir">Base directory</Label>
                  <Input
                    id="app-basedir"
                    placeholder="/ (monorepo subfolder, e.g. /apps/web)"
                    value={state.baseDirectory}
                    onChange={(e) => update({ baseDirectory: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <Label htmlFor="app-install">Install command</Label>
                  <Input
                    id="app-install"
                    placeholder="auto-detected"
                    value={state.installCommand}
                    onChange={(e) => update({ installCommand: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="app-build">Build command</Label>
                  <Input
                    id="app-build"
                    placeholder="auto-detected"
                    value={state.buildCommand}
                    onChange={(e) => update({ buildCommand: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="app-start">Start command</Label>
                  <Input
                    id="app-start"
                    placeholder="auto-detected"
                    value={state.startCommand}
                    onChange={(e) => update({ startCommand: e.target.value })}
                  />
                </div>
              </div>
              {(state.buildPack === 'static' || state.buildPack === 'nixpacks') && (
                <div>
                  <Label htmlFor="app-publish">Publish directory</Label>
                  <Input
                    id="app-publish"
                    placeholder="e.g. /dist for static builds"
                    value={state.publishDirectory}
                    onChange={(e) => update({ publishDirectory: e.target.value })}
                  />
                </div>
              )}
            </>
          )}

          {stepKey === 'Network & health' && (
            <>
              <div>
                <Label htmlFor="app-domains">Domains (one URL per line)</Label>
                <Textarea
                  id="app-domains"
                  rows={3}
                  placeholder={'https://app.example.com'}
                  disabled={!features.custom_domains}
                  value={state.domainsText}
                  onChange={(e) => update({ domainsText: e.target.value })}
                />
                {!features.custom_domains && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    The plan does not include custom domains — a domain will be auto-generated.
                  </p>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                    checked={state.autogenerateDomain}
                    onChange={(e) => update({ autogenerateDomain: e.target.checked })}
                  />
                  Auto-generate a domain when none is set
                </label>
                <div>
                  <Label htmlFor="app-ports">Exposed ports</Label>
                  <Input
                    id="app-ports"
                    placeholder="3000 or 3000,8080"
                    value={state.portsExposes}
                    onChange={(e) => update({ portsExposes: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                    checked={state.healthCheckEnabled}
                    onChange={(e) => update({ healthCheckEnabled: e.target.checked })}
                  />
                  Enable health check
                </label>
                {state.healthCheckEnabled && (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="hc-path">Path</Label>
                      <Input
                        id="hc-path"
                        value={state.healthCheckPath}
                        onChange={(e) => update({ healthCheckPath: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="hc-port">Port</Label>
                      <Input
                        id="hc-port"
                        placeholder="defaults to the first exposed port"
                        value={state.healthCheckPort}
                        onChange={(e) => update({ healthCheckPort: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {stepKey === 'Review' && (
            <>
              <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                {[
                  ['Name', state.name],
                  ['Source', SOURCE_OPTIONS.find((o) => o.key === state.source)?.label ?? ''],
                  ...(isGit
                    ? ([
                        ['Repository', state.gitRepository],
                        ['Branch', state.gitBranch],
                        ['Build pack', state.buildPack],
                      ] as const)
                    : []),
                  ...(state.source === 'dockerimage'
                    ? ([['Image', `${state.dockerImageName}:${state.dockerImageTag}`]] as const)
                    : []),
                  ['Server', servers.data?.find((s) => s.uuid === state.serverUuid)?.name ?? state.serverUuid],
                  ['Domains', state.domainsText.trim() || (state.autogenerateDomain ? 'auto-generated' : '—')],
                  ['Ports', state.portsExposes || '—'],
                  [
                    'Container limits',
                    `${detail.data.limits.memory_mb == null ? 'unlimited memory' : `${detail.data.limits.memory_mb} MB`}, ${detail.data.limits.cpu_cores == null ? 'unlimited CPU' : `${detail.data.limits.cpu_cores} CPU`} (from plan)`,
                  ],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-3 border-b border-slate-100 py-1.5 dark:border-slate-800">
                    <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                    <dd className="text-right font-medium">{value}</dd>
                  </div>
                ))}
              </dl>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                  disabled={!features.deployments}
                  checked={state.instantDeploy}
                  onChange={(e) => update({ instantDeploy: e.target.checked })}
                />
                Deploy immediately after creation
                {!features.deployments && (
                  <span className="text-xs text-amber-600 dark:text-amber-400">
                    (plan does not include deployments)
                  </span>
                )}
              </label>
            </>
          )}

          {create.error != null && <ErrorNote error={create.error} />}

          <div className="flex items-center justify-between border-t border-slate-100 pt-4 dark:border-slate-800">
            <Button variant="secondary" disabled={step === 0} onClick={() => setStep(step - 1)}>
              <ArrowLeft className="size-4" /> Back
            </Button>
            {stepKey !== 'Review' ? (
              <Button disabled={!canNext} onClick={() => setStep(step + 1)}>
                Next <ArrowRight className="size-4" />
              </Button>
            ) : (
              <Button
                loading={create.isPending}
                disabled={!subscription.coolifyProjectUuid}
                onClick={() => create.mutate(toCreateInput(state, subscriptionId!))}
              >
                <Rocket className="size-4" /> Create application
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
